import dayjs from "dayjs";
import type { Interval, Section, Session } from "@models/ISchema";
import { compareIds } from "./shared";

/** One session's occupied blocks on a particular day of the selected week. */
export interface AnnualOccurrence {
  id: string;
  date: string;
  startingTime: string;
  endingTime: string;
  blocksCount: number;
  instructors: Session["instructors"];
  classrooms: Session["classrooms"];
}

type PendingOccurrence = Omit<AnnualOccurrence, "blocksCount"> & { blocks: Set<string> };

const days = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

/**
 * Builds drawer details for one component and week from calendar sessions.
 *
 * @param sections - Calendar sections linked to the selected league.
 * @param componentId - Component represented by the matrix row.
 * @param week - Week represented by the matrix column.
 * @returns Sessions grouped by week and day, sorted by date and start time.
 *
 * @remarks This mirrors the existing scheduleInstances resolver: a group's date is
 * derived from the week's start and the block's day, and its time range spans the
 * earliest start to the latest end. Unknown days or undated weeks are ignored.
 */
export const getCellOccurrences = (
  sections: Section[],
  componentId: string,
  week: Interval,
): AnnualOccurrence[] => {
  const occurrences = new Map<string, PendingOccurrence>();

  sections
    ?.filter(section => section?.component?.id === componentId)
    .forEach(section => {
      section.sessions?.forEach(session =>
        session.events?.forEach(event => {
          const block = event.block;

          if (!block || event.interval?.id !== week.id || !week.startingDate) return;

          const dayIndex = days.indexOf(block.day);
          if (dayIndex < 0) return;

          const id = JSON.stringify([session.id, week.id, block.day]);
          const occurrence = occurrences.get(id) ?? {
            id,
            date: dayjs(week.startingDate.slice(0, 10)).add(dayIndex, "day").format("YYYY-MM-DD"),
            startingTime: block.startingTime ?? "",
            endingTime: block.endingTime ?? "",
            blocks: new Set<string>(),
            instructors: session.instructors,
            classrooms: session.classrooms,
          };

          occurrence.blocks.add(block.id);
          if (
            block.startingTime &&
            (!occurrence.startingTime || block.startingTime < occurrence.startingTime)
          ) {
            occurrence.startingTime = block.startingTime;
          }

          if (block.endingTime && block.endingTime > occurrence.endingTime) {
            occurrence.endingTime = block.endingTime;
          }

          occurrences.set(id, occurrence);
        }),
      );
    });

  return [...occurrences.values()]
    .map(({ blocks, ...occurrence }) => ({ ...occurrence, blocksCount: blocks.size }))
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        a.startingTime.localeCompare(b.startingTime) ||
        compareIds(a.id, b.id),
    );
};
